/**
 * TEMPLATE-BENCHMARK-SIDEBAR
 * Disponibilité benchmark par secteur pour le template en cours d'édition dans l'éditeur.
 * Lecture seule (v1) : aucune écriture depuis ce panneau, hormis le choix du secteur du compte.
 *
 * Structure (jauge en pilule, encadré pointillé si secteur non défini) reprise du mockup
 * Claude Design "Benchmark editor sidebar" — couleurs, elles, ramenées aux vraies variables
 * IDTree (src/style/colors.css), pas la palette violette générique du mockup.
 */
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlassChart, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import Link from "@/components/link"
import PageLoader from "@/components/page-loader"
import Tooltip from "@/components/tooltip"
import SectorPickerModal from "./modals/SectorPickerModal"
import { SECTOR_ICONS } from "./modals/SectorGrid"
import SupervisorUnmatchedQuestionsModal from "./modals/SupervisorUnmatchedQuestionsModal"
import { selectCanSeeBenchmark } from "@/features/benchmark/entities/question/model/benchmarkQuestion.selector"
import {
  fetchTemplateBenchmarkStatus,
  updateAccountSector,
  updateAccountSector_AsSupervisor,
  TemplateBenchmarkStatus
} from "@/features/benchmark/entities/question/model/benchmarkQuestion.thunk"

export default function TemplateBenchmarkSidebar() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const canSee = useAppSelector(selectCanSeeBenchmark)
  const _session = useAppSelector((state) => state._session)
  const template = useAppSelector((state) => state.template)
  const topic = useAppSelector((state) => state.topic)
  const axis = useAppSelector((state) => state.axis)
  const templateId = template.active.id

  const [status, setStatus] = useState<TemplateBenchmarkStatus | null>(null)
  //Only tracks the very first fetch — a background refresh triggered by editing a question
  //(debounced effect below) must not flip the panel back to a spinner and flicker
  const [loadStatus, setLoadStatus] = useState<string>(STATUS_LOADING)
  //Raw supervisor only — the client / connected-as-supervisor sector picker lives inline inside
  //SupervisorUnmatchedQuestionsModal instead, to avoid stacking two modals on top of each other
  const [sectorModalOpen, setSectorModalOpen] = useState(false)
  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstSignature = useRef(true)
  const wasEdited = useRef(topic.isEdited)

  //Signature of every scale question's FR text — refetch the status whenever it changes
  //(a question edited in the card above must fall back to "sans benchmark" live, same idea
  //as the benchmarkAvailable reset already done in editor-question.widget.tsx)
  const questionsSignature = topic.list
    .flatMap((t) => [t.questionPrimary, ...t.questionsSecondary])
    .filter((q) => q && q.type === "scale")
    .map((q) => q!.name?.fr || "")
    .join("|")

  //Raw supervisor (editing a template directly from /supervisor/templates, no client
  //impersonation) has no accountId on its JWT at all — goes through the /supervisor/benchmark/*
  //routes instead, same split as templateUpdate/templateUpdate_AsSupervisor in template.actions.ts
  const isRawSupervisor = _session.interfaceType === "SUPERVISOR"

  async function load() {
    if (!templateId || !canSee) return
    const res = await dispatch(fetchTemplateBenchmarkStatus({ templateId, asSupervisor: isRawSupervisor })).unwrap()
    if (res) setStatus(res)
    setLoadStatus(STATUS_LOADED)
  }

  //Initial load / template change
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, canSee])

  //Live refresh, debounced, whenever a question's text changes — skip the very first run, already
  //covered by the mount effect above (both effects otherwise fire together on mount)
  useEffect(() => {
    if (isFirstSignature.current) {
      isFirstSignature.current = false
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(load, 600)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsSignature])

  //The debounced refresh above fires on the local (unsaved) edit, well before "Sauvegarder"
  //actually persists it — the backend still returns the pre-edit status at that point. Refresh
  //again once topic.isEdited flips back to false, which only happens after topicUpdateAndSave's
  //PATCH genuinely succeeds (topic.actions.ts topicSave(), dispatched only in the non-error branch)
  useEffect(() => {
    if (wasEdited.current && !topic.isEdited) load()
    wasEdited.current = topic.isEdited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic.isEdited])

  async function selectSector(sector: string | null) {
    setSectorModalOpen(false)
    if (isRawSupervisor) {
      if (!status?.accountId) return //master template, no account to attach a sector to
      await dispatch(updateAccountSector_AsSupervisor({ accountId: status.accountId, sector }))
    } else {
      await dispatch(updateAccountSector(sector))
    }
    load()
  }

  if (!canSee) return null

  const pct = status ? (status.total ? Math.round((status.withBenchmark / status.total) * 100) : 0) : 0
  //Association (search/link/unlink) stays supervisor-only, impersonation included
  const canAssociate = isRawSupervisor || _session.connectedAsSupervisor
  //Everyone else (normal client, or a CS connected as that client) gets the new unified card:
  //no more inline sector line, sector management moved into the modal, always clickable to open
  //it — the raw supervisor card (no client impersonation) keeps its own layout below unchanged
  const unified = !isRawSupervisor
  //A master/library template (no owning account at all) has no sector to define — raw supervisor
  //only, `unified` sessions always have a real account behind them
  const canDefineSector = status ? unified || !!status.accountId : false
  const canOpenModal = status ? (unified ? true : canAssociate && status.total > 0) : false

  return (
    <div style={{ margin: "20px 20px 0" }}>
      {sectorModalOpen && status && (
        <SectorPickerModal current={status.sector} onClose={() => setSectorModalOpen(false)} onSelect={selectSector} />
      )}

      {unmatchedModalOpen && status && (
        <SupervisorUnmatchedQuestionsModal
          questions={status.supervisorQuestions}
          noBenchmarkQuestions={status.noBenchmarkQuestions}
          axisList={axis.list}
          topicList={topic.list}
          asRawSupervisor={isRawSupervisor}
          canAssociate={canAssociate}
          canManageSector={unified}
          sector={status.sector}
          onSelectSector={selectSector}
          onClose={() => setUnmatchedModalOpen(false)}
          onLinked={load}
        />
      )}

      <PageLoader status={loadStatus} minHeight={64}>
        {status && (
          <div
            className={`flex flex-col gap-2 ${canOpenModal ? "_hover" : ""}`}
            style={{
              background: "#f6f6f6",
              border: "1px solid #CCCCCC",
              borderRadius: 8,
              padding: 10,
              cursor: canOpenModal ? "pointer" : undefined
            }}
            onClick={() => canOpenModal && setUnmatchedModalOpen(true)}>
            <div className="flex items-center justify-between font-semibold" style={{ color: "#111C2B" }}>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faMagnifyingGlassChart} />
                {t("benchmark_editor_panel_title")}
              </span>
              {status.sector && SECTOR_ICONS[status.sector] && (
                <Tooltip text={t("benchmark_sector_" + status.sector)} position="left">
                  <FontAwesomeIcon icon={SECTOR_ICONS[status.sector]} className="grey-t" />
                </Tooltip>
              )}
            </div>

            {!unified && !status.sector && canDefineSector && (
              <>
                <p className="m-0 grey-t">{t("benchmark_editor_sector_undefined")}</p>
                <Link
                  isWithoutMargin
                  icon={faArrowRight}
                  onClick={(e: any) => {
                    e.stopPropagation()
                    setSectorModalOpen(true)
                  }}>
                  {t("benchmark_editor_sector_define")}
                </Link>
              </>
            )}

            {!unified && status.sector && (
              <div className="flex items-center gap-1 flex-wrap grey-t">
                <b>{t("benchmark_editor_sector_label")}</b> :
                <Link
                  isWithoutMargin
                  onClick={(e: any) => {
                    e.stopPropagation()
                    setSectorModalOpen(true)
                  }}>
                  {t("benchmark_sector_" + status.sector)}
                </Link>
              </div>
            )}

            {(unified || status.sector || !canDefineSector) && (
              <>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span
                      className="shrink-0"
                      style={{ width: 10, height: 10, borderRadius: 3, background: "#20CA7E" }}
                    />
                    <span>
                      <b>{status.withBenchmark}</b> {t("benchmark_editor_with_benchmark")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 grey-t text-sm">
                    <span
                      className="shrink-0"
                      style={{ width: 10, height: 10, borderRadius: 3, background: "#CCCCCC" }}
                    />
                    <span>
                      <b>{status.withoutBenchmark}</b> {t("benchmark_editor_without_benchmark")}
                    </span>
                  </div>
                  {status.noBenchmarkQuestions.length > 0 && (
                    <div className="flex items-center gap-2 grey-t text-sm">
                      <span
                        className="shrink-0"
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: "#f6f6f6",
                          border: "1px solid #CCCCCC",
                          boxSizing: "border-box"
                        }}
                      />
                      <span>
                        <b>{status.noBenchmarkQuestions.length}</b> {t("benchmark_editor_no_notion")}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className="flex overflow-hidden"
                    style={{ flex: 1, height: 14, borderRadius: 999, background: "#CCCCCC" }}>
                    <div className="transition-all" style={{ background: "#20CA7E", width: pct + "%", minWidth: 0 }} />
                  </div>
                  <span style={{ fontWeight: 700, color: "#111C2B", whiteSpace: "nowrap" }}>{pct}%</span>
                </div>
              </>
            )}
          </div>
        )}
      </PageLoader>
    </div>
  )
}
